import { useQuery } from '@apollo/client/react'
import type { ScoProduct } from '@src/graphql-types'
import { gql } from '@src/lib/gql'
import { useMemo } from 'react'

const PRICING_FIELDS = `
  signup
  recurring
  trialDays
  trialRequiresCard
  prepaidPeriods
  prepaidPeriodsUnit
  nextAssessmentAt
`

/**
 * Every ScoProduct field the Conversion kit reads. One query for gates, pages, and checkout so the
 * product a wall names is the product the API priced (coupon and term applied server-side).
 */
export const CONVERSION_PRODUCTS_QUERY = gql`
  query ConversionProducts($productIds: [String], $termId: String, $couponCode: String) {
    scoProducts(productIds: $productIds, termId: $termId, couponCode: $couponCode) {
      id
      productId
      title
      paywallName
      termId
      effectiveTermId
      specialTermsNote
      statementOfWorkUrl
      appliedCoupon {
        code
      }
      pricingInCents {
        ${PRICING_FIELDS}
      }
      effectivePricingInCents {
        ${PRICING_FIELDS}
      }
      effectiveTerm {
        abbreviation
        name
        termId
      }
      terms {
        termId
        name
        months
        abbreviation
        pricingInCents {
          signup
          recurring
        }
      }
      features {
        id
        name
      }
      paywallOptions {
        title
        description
        features {
          slug
          title
          description
        }
      }
    }
  }
`

export type UseConversionProductsOptions = {
  termId?: string | null
  couponCode?: string | null
  skip?: boolean
}

/**
 * Loads live catalog products in the order requested. Unknown ids are dropped (the API returns a
 * synthetic `unknown` product rather than null), so a caller never renders a $0 placeholder plan.
 */
export function useConversionProducts(
  productIds: Array<string | null | undefined>,
  { termId, couponCode, skip }: UseConversionProductsOptions = {}
) {
  // Callers usually pass an inline array; key on its contents so the query variables stay stable.
  const idsKey = Array.from(new Set(productIds.map(id => id?.trim()).filter(Boolean) as string[])).join('|')
  const ids = useMemo(() => (idsKey ? idsKey.split('|') : []), [idsKey])

  const query = useQuery(CONVERSION_PRODUCTS_QUERY, {
    skip: skip || ids.length === 0,
    variables: {
      productIds: ids,
      termId: termId?.trim() || undefined,
      couponCode: couponCode?.trim() || undefined,
    },
  })

  const products = useMemo(() => {
    const returned = ((query.data?.scoProducts || []) as Array<ScoProduct | null>).filter(
      (product): product is ScoProduct => Boolean(product?.productId) && product?.productId !== 'unknown'
    )
    return ids
      .map(id => returned.find(product => product.productId === id))
      .filter((product): product is ScoProduct => Boolean(product))
  }, [ids, query.data])

  return {
    products,
    loading: query.loading,
    error: query.error ?? null,
    refetch: query.refetch,
  }
}
